import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { Box } from '@chakra-ui/react';
import { useSetDisciplinaryActionMetaFiltersInitialData } from 'Deer/intent/DisciplinaryActions/hooks/disciplinaryActionGrid';
import {
  useSelectDisciplinaryActionsSelectedCompanies,
  useSelectDisciplinaryActionsSelectedLocations,
} from 'Deer/intent/DisciplinaryActions/slice/DisciplinaryActionsIntentSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import {
  CellRenderer,
  getColumnDefinitionByType,
} from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

export const NONE_ID = 'NONE';

const customGroupCellRenderer = (params) => {
  const {
    colDef: { cellRendererParams },
  } = params || { colDef: {} };
  const { columns } = cellRendererParams || {};

  const { colDef } = params ?? {};

  const curColumn = columns.find((col) => col.columnId === colDef.field) || {};

  const modifiedParams = {
    ...params,
    colDef: {
      ...params.colDef,
      ...curColumn,
      colId: curColumn.columnId,
    },
  };

  return CellRenderer(modifiedParams);
};

const FILTERED_COLUMNS_IDS_ADMIN_REPORTS = [
  'EMPLOYEE',
  'TOTAL_VIOLATIONS',
  'UNACKNOWLEDGED_NOTES',
  'VIOLATIONS_BY_TYPE',
  'VIOLATIONS_BY_SEVERITY',
];

export const FILTERED_REPORT_COLUMNS_IDS_ADMIN_REPORTS = [
  ...FILTERED_COLUMNS_IDS_ADMIN_REPORTS,
  'EMAIL_INFO',
  'MANAGER_EMAIL_INFO',
];

const MyCompanyAdminReportsGrid = ({ gridRef, gridStatePersistenceKey, adminReportsData }) => {
  const [columnDefs, setColumnDefs] = useState([]);

  useSetDisciplinaryActionMetaFiltersInitialData(adminReportsData?.entity ?? {});
  const selectedCompanies = useSelectDisciplinaryActionsSelectedCompanies();
  const selectedLocations = useSelectDisciplinaryActionsSelectedLocations();

  const { rows, columns } = adminReportsData?.entity || {};

  const filteredRows = useMemo(() => {
    if (checkEnableCompanySupport()) {
      const selectedCompaniesMap = {};
      const selectedLocationsMap = {};
      selectedCompanies?.forEach((company) => {
        selectedCompaniesMap[company.id] = company;
      });
      selectedLocations?.forEach((location) => {
        selectedLocationsMap[location?.toLowerCase()] = location;
      });

      return rows?.filter(
        (row) => selectedCompaniesMap[row?.COMPANY?.id] && selectedLocationsMap[row?.LOCATION?.toLowerCase()]
      );
    }
    return rows;
  }, [rows, selectedCompanies, selectedLocations]);

  // set the column definitions
  // based on the selected view by and split by
  // and the columns from the API
  useEffect(() => {
    if (!columns || columns.length === 0) return;
    const newColumnDefs = columns.map((col) => {
      const colProps = col;
      const additionalProps: {
        filter?: string | boolean;
        rowGroup?: boolean;
        pivot?: boolean;
        aggFunc?: string;
        hide?: boolean;
        flex?: number;
        suppressColumnsToolPanel?: boolean;
        filterParams?: {
          values?: string[];
          getValue?: (data: any) => string;
        };
        valueGetter?: (data: any) => string;
      } = {};
      if (!FILTERED_COLUMNS_IDS_ADMIN_REPORTS.includes(col.columnId)) {
        additionalProps.hide = true;
        additionalProps.suppressColumnsToolPanel = true;
      }

      if (colProps.columnId === 'EMPLOYEE') {
        additionalProps.flex = 1;
        colProps.metadata = { isEmployeeSubtitle: true };
      }
      if (colProps.columnId === 'VIOLATIONS_BY_SEVERITY') {
        additionalProps.filter = 'agSetColumnFilter';

        additionalProps.filterParams = {
          values: ['high', 'medium', 'low'],
          getValue: (params) => {
            const violations = params?.data?.VIOLATIONS_BY_SEVERITY;
            const violationsLabels = violations?.map((violation) => violation?.label?.toLowerCase());
            return violationsLabels;
          },
        };
        additionalProps.valueGetter = (params) => {
          const violations = params?.data?.VIOLATIONS_BY_SEVERITY;
          const violationsLabels = violations?.map((violation) => violation?.label?.toLowerCase()) ?? [];
          return violationsLabels.join(', ');
        };
      }
      if (colProps.columnId === 'VIOLATIONS_BY_TYPE') {
        additionalProps.valueGetter = (params) => {
          const violations = params?.data?.VIOLATIONS_BY_TYPE ?? {};
          const violationsLabels = Object.entries(violations)?.map(([key, value]) => `${key}: ${value}`) ?? [];
          return violationsLabels.join(', ');
        };
      }

      return {
        colId: colProps.columnId,
        field: colProps.columnId,
        id: colProps.columnId,
        displayName: colProps.displayName,
        headerName: colProps.displayName,
        description: colProps.description,
        minWidth: 220,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: colProps.columnId !== 'VIOLATIONS_BY_TYPE',
          enableSorting: false,
        },

        ...getColumnDefinitionByType(colProps),
        sortable: false,
        ...additionalProps,
      };
    });
    setColumnDefs(newColumnDefs);
  }, [columns]);

  const autoGroupColumnDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 280,
      field: 'EMPLOYEE',
      colId: 'EMPLOYEE',
      headerName: 'Employee',
      cellRenderer: customGroupCellRenderer,
      sortable: false,
      cellRendererParams: {
        columns,
      },
      headerComponent: CustomHeaderCell,
      headerComponentParams: {
        enableMenu: true,
        enableSorting: false,
      },
    }),
    [columns]
  );

  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
      wrapText: true,
      resizable: false,
    }),
    []
  );

  // At the component level, add a ref to store row heights
  const rowHeightsRef = useRef({});

  // Create a getRowHeight callback function
  const getRowHeight = useCallback((params) => rowHeightsRef.current[params.node.id] || 80, []);

  const calculateRowHeights = useCallback(() => {
    // Find all cells containing split-progress-graph components
    const setSplitGraphCells = document.querySelectorAll('.admin-reports-grid .split-progress-graph');
    const newRowHeights = {};

    // Process each cell with split-progress-graph
    setSplitGraphCells.forEach((cell) => {
      const rowElement = cell.closest('.ag-row');
      if (rowElement) {
        const rowId = rowElement.getAttribute('row-id');
        // Get the cell's actual height with padding
        const cellHeight = cell.offsetHeight + 20;

        // Update the maximum height needed for this row
        newRowHeights[rowId] = Math.max(newRowHeights[rowId] || 80, cellHeight);
      }
    });

    // Update our ref with new heights
    rowHeightsRef.current = newRowHeights;

    // If we have grid API, reset heights
    if (gridRef?.current?.api && setSplitGraphCells.length > 0) {
      gridRef.current.api.redrawRows();
    }
  }, [gridRef]);

  const onFirstDataRendered = () => {
    // Allow a small delay for DOM rendering
    setTimeout(calculateRowHeights, 100);
  };

  const onCellValueChanged = () => {
    setTimeout(calculateRowHeights, 100);
  };

  return (
    <Box className="admin-reports-grid">
      <AgGridTable
        key={gridStatePersistenceKey}
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={filteredRows}
        domLayout="normal"
        pagination
        paginationPageSize={10}
        headerHeight={36}
        getRowHeight={getRowHeight}
        kebabMenuIsRowSpecific={false}
        suppressAggFuncInHeader
        autoGroupColumnDef={autoGroupColumnDef}
        defaultColDef={defaultColDef}
        onFirstDataRendered={onFirstDataRendered}
        onCellValueChanged={onCellValueChanged}
      />
    </Box>
  );
};

export { MyCompanyAdminReportsGrid };
